import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { map, Observable, ReplaySubject, shareReplay, tap } from 'rxjs';
import { AuthService, ROL_ADMIN, ROL_AUDITOR, ROL_SUPERVISOR } from '../auth/auth.service';

const funcionarioRoutes: FuseNavigationItem[] = [
  {
    id: 'dashboards',
    title: '',
    subtitle: '',
    type: 'group',
    icon: 'heroicons_outline:home',
    children: [
      {
        id: 'dashboards.project',
        title: 'Módulo',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/funcionariogeneral',
      },
    ],
  },
];

const supervisorRoutes: FuseNavigationItem[] = [
  {
    id: 'catalogos',
    title: 'Catálogos',
    type: 'group',
    icon: 'heroicons_outline:document-duplicate',
    children: [
      {
        id: 'grupos',
        title: 'Grupos',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablagrupo',
      },
      {
        id: 'perfiles',
        title: 'Perfiles',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablaperfil',
      },
      {
        id: 'modulos',
        title: 'Módulos',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablamodulo',
      },
      {
        id: 'salas',
        title: 'Salas',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablasala',
      },
      {
        id: 'kioskos',
        title: 'Kioskos',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablakiosko',
      },
      {
        id: 'multimedia',
        title: 'Multimedia',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablamultimedia',
      },
      {
        id: 'tramites',
        title: 'Trámites',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablatramites',
      },
      {
        id: 'usuarios',
        title: 'Usuarios',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablausuario',
      },
    ]
  },
];

const adminRoutes: FuseNavigationItem[] = [
  {
    id: 'admin',
    title: 'Admin',
    type: 'group',
    icon: 'heroicons_outline:document-duplicate',
    children: [
      {
        id: 'sucursales',
        title: 'Sucursales',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablasucursal',
      }
    ]
  },
];

const reporteRoutes: FuseNavigationItem[] = [
  {
    id: 'reportes',
    title: 'Reportes',
    type: 'group',
    icon: 'heroicons_outline:document-duplicate',
    children: [
      {
        id: 'turnos',
        title: 'Turnos',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/reporte-turnos',
      }
    ]
  },
];

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private authService = inject(AuthService)
  private _httpClient = inject(HttpClient);
  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation
   */
  get navigation$(): Observable<Navigation> {
    return this.authService.getUser().pipe(
      map(user => {
        const routes: FuseNavigationItem[] = [];
        if (user.requiereCambioPass) {
          return routes;
        }

        if (user.puestoAsignado) {
          routes.push(...funcionarioRoutes);
        }

        let isAdmin = false;
        let isSupervisor = false;
        let isAuditor = false;
        for (const rol of user.roles) {
          if (rol.clave === ROL_ADMIN) {
            isAdmin = true;
            break;
          }

          if (rol.clave === ROL_SUPERVISOR) {
            isSupervisor = true;
          }

          if (rol.clave === ROL_AUDITOR) {
            isAuditor = true;
          }
        }

        if (isAdmin) {
          routes.push(...supervisorRoutes);
          routes.push(...adminRoutes);
          routes.push(...reporteRoutes);
        } else {
          if (isSupervisor) {
            routes.push(...supervisorRoutes);
          }
          
          if (isAuditor) {
            routes.push(...reporteRoutes);
          }
        }

        return routes;
      }),
      map(routes => ({
        compact: routes,
        default: routes,
        futuristic: routes,
        horizontal: routes,
      })),
      shareReplay(1)
    );
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Observable<Navigation> {
    return this._httpClient.get<Navigation>('api/common/navigation').pipe(
      tap((navigation) => {
        this._navigation.next(navigation);
      }),
    );
  }
}
